import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import ReplyIcon from '@mui/icons-material/Reply';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { NewMessageEvent } from 'pages/Clients';
import React from 'react';
import type { Client } from 'services/types/client';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface ClientSidebarProps {
  clients: Client[];
  currentClientId: string;
  newEvents: NewMessageEvent[];
  onClose: () => void;
  onClientSelect: (clientId: number) => void;
}

const ClientSidebar: React.FC<ClientSidebarProps> = ({
  clients,
  currentClientId,
  newEvents,
  onClose,
  onClientSelect,
}) => {
  const currentClientIdNumber = Number(currentClientId.replace('.', ''));
  return (
    <Paper
      sx={{
        maxWidth: '250px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: SURFACE,
        zIndex: 200,
        boxShadow: SHADOW,
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          width: '250px',
          backgroundColor: SURFACE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '60px',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY, fontSize: '1rem' }}>
          Lista de Clientes
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: alpha(NAVY, 0.55) }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {/* Clients List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 0.75 }}>
        <List sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {clients.map((client, index) => {
            const isCurrentClient = client.id === currentClientIdNumber;
            const clientEvents = newEvents.filter((event) => event.clientId === client.id);

            return (
              <React.Fragment key={client.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => onClientSelect(client.id)}
                    sx={{
                      backgroundColor: isCurrentClient ? SURFACE_CONTAINER_LOWEST : 'transparent',
                      borderRadius: RADIUS_CARD,
                      boxShadow: isCurrentClient ? `0px 1px 4px rgba(21, 24, 58, 0.05)` : 'none',
                      borderLeft: isCurrentClient ? `3px solid ${YELLOW}` : '3px solid transparent',
                      py: 1,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: SURFACE_CONTAINER_LOWEST,
                      },
                      transition: 'all 0.15s ease-in-out',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isCurrentClient ? 600 : 400,
                          color: NAVY,
                          fontSize: '0.85rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {client.firstName} {client.lastName}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(NAVY, 0.45),
                          fontSize: '0.7rem',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        {[client.phone, client.whatsAppUserId && `Meta: ${client.whatsAppUserId}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MessageIcon sx={{ fontSize: 11, color: alpha(NAVY, 0.35) }} />
                          <Typography variant="caption" sx={{ color: alpha(NAVY, 0.45), fontSize: '0.7rem' }}>
                            {client.messagesCount}
                          </Typography>
                        </Box>
                        {client.clientMessagesCount > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <ReplyIcon sx={{ fontSize: 11, color: alpha(NAVY, 0.5) }} />
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: alpha(NAVY, 0.45) }}>
                              x{client.clientMessagesCount}
                            </Typography>
                          </Box>
                        )}
                        {clientEvents.length > 0 && (
                          <Box
                            sx={{
                              backgroundColor: '#53B721',
                              color: SURFACE_CONTAINER_LOWEST,
                              borderRadius: RADIUS_ITEM,
                              px: 0.75,
                              py: 0.125,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            {clientEvents.length > 1 ? `${clientEvents.length} nuevos` : 'Nuevo'}
                          </Box>
                        )}
                        {clientEvents.length === 0 && client.unreadMessagesCount > 0 && (
                          <Box
                            sx={{
                              backgroundColor: alpha('#E7233B', 0.12),
                              color: '#E7233B',
                              borderRadius: RADIUS_ITEM,
                              px: 0.75,
                              py: 0.125,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            Pendiente
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < clients.length - 1 && <Box sx={{ mx: 2, borderBottom: `1px solid ${alpha(NAVY, 0.06)}` }} />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
      {/* Footer */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontSize: '0.65rem' }}>
          {clients.length} clientes (los que estaban en la lista donde se abrió el chat)
        </Typography>
      </Box>
    </Paper>
  );
};

export default ClientSidebar;
